// AEON — Manual Paper Trading engine (pure, no React, no I/O).
//
// Everything here is a pure function of (state, event, quotes). The engine never
// touches the network, the DOM, or real exchange keys — it is a deterministic
// simulator so it can be unit-tested and persisted verbatim (see paperStore.js).
//
// This module is intentionally separate from the autonomous-engine paper
// accounts served by the backend (/api/paper/*). Nothing here writes to that
// system; account ids live in their own namespace.

// ─── Constants ────────────────────────────────────────────────────────────

export const SIDE = { LONG: "long", SHORT: "short" };
export const ORDER_SIDE = { BUY: "buy", SELL: "sell" };
export const ORDER_TYPE = { MARKET: "market", LIMIT: "limit", STOP: "stop" };
export const TIF = { GTC: "GTC", DAY: "Day", WEEK: "Week" };
export const STATUS = {
  WORKING: "working",
  INACTIVE: "inactive",
  FILLED: "filled",
  CANCELLED: "cancelled",
  REJECTED: "rejected",
};
export const ROLE = { ENTRY: "entry", TP: "tp", SL: "sl" };

export const DEFAULT_SETTINGS = {
  defaultLeverage: 10,
  takerFeeRate: 0.0004, // 0.04% taker
  makerFeeRate: 0.0002, // 0.02% maker
  slippageBps: 0, // extra slippage on market fills, in basis points
};

const DEFAULT_START_BALANCE = 100000;

// ─── ID / time helpers (injectable for deterministic tests) ─────────────────

let _seq = 0;
export function nextId(prefix = "id") {
  _seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${_seq.toString(36)}`;
}

// ─── State construction ─────────────────────────────────────────────────────

export function makeAccount({
  id = nextId("acct"),
  name = "Paper",
  startingBalance = DEFAULT_START_BALANCE,
  now = Date.now(),
} = {}) {
  return {
    id,
    name,
    currency: "USD",
    startingBalance,
    realizedPnl: 0,
    feesPaid: 0,
    positions: {}, // symbol -> Position
    orders: {}, // orderId -> Order
    leverage: {}, // symbol -> number
    activity: [], // Event[]
    trades: [], // Trade[] (round-trip legs)
    balanceHistory: [{ t: now, balance: startingBalance, equity: startingBalance }],
    createdAt: now,
  };
}

export function makeState({ settings = {}, now = Date.now() } = {}) {
  const first = makeAccount({ name: "Paper 1", now });
  return {
    version: 1,
    settings: { ...DEFAULT_SETTINGS, ...settings },
    accounts: { [first.id]: first },
    activeAccountId: first.id,
  };
}

// ─── Small pure utilities ───────────────────────────────────────────────────

const round = (n, dp = 8) => {
  const f = 10 ** dp;
  return Math.round((n + Number.EPSILON) * f) / f;
};

function log(account, type, message, data = {}, now = Date.now()) {
  account.activity.unshift({ id: nextId("evt"), t: now, type, message, ...data });
}

// clone helpers — the reducer works on a structuredClone so callers can treat
// results as immutable snapshots.
function clone(x) {
  return typeof structuredClone === "function"
    ? structuredClone(x)
    : JSON.parse(JSON.stringify(x));
}

function activeAccount(state) {
  return state.accounts[state.activeAccountId];
}

export function getLeverage(account, symbol, settings) {
  return account.leverage[symbol] || settings.defaultLeverage;
}

// A quote is { bid, ask, last }. Derive whatever is missing from last.
function normQuote(q) {
  if (!q) return null;
  const last = q.last != null ? q.last : q.bid != null ? q.bid : q.ask;
  if (last == null) return null;
  return {
    last,
    bid: q.bid != null ? q.bid : last,
    ask: q.ask != null ? q.ask : last,
  };
}

function fillPriceFor(side, quote, slippageBps) {
  const slip = (slippageBps || 0) / 10000;
  if (side === ORDER_SIDE.BUY) return quote.ask * (1 + slip);
  return quote.bid * (1 - slip);
}

// ─── PnL / margin math ──────────────────────────────────────────────────────

export function positionNotional(pos, price) {
  return Math.abs(pos.qty) * price;
}

export function unrealizedPnl(pos, price) {
  if (!pos || pos.qty === 0 || price == null) return 0;
  const dir = pos.side === SIDE.LONG ? 1 : -1;
  return round(dir * (price - pos.avgPrice) * pos.qty);
}

export function positionMargin(pos, leverage) {
  // Margin locked at entry price (notional at entry / leverage).
  return round((pos.avgPrice * pos.qty) / leverage);
}

// Margin reserved by a working entry order (limit/stop, not reduce-only).
function orderMargin(order, leverage) {
  if (order.reduceOnly) return 0;
  if (order.status !== STATUS.WORKING && order.status !== STATUS.INACTIVE) return 0;
  const px = order.limitPrice != null ? order.limitPrice : order.stopPrice;
  if (px == null) return 0;
  return round((px * order.qty) / leverage);
}

// Full account rollup given the latest prices. Pure; no mutation.
export function computeAccount(account, prices = {}, settings = DEFAULT_SETTINGS) {
  let unreal = 0;
  let accountMargin = 0;
  for (const sym of Object.keys(account.positions)) {
    const pos = account.positions[sym];
    if (!pos || pos.qty === 0) continue;
    const price = prices[sym] != null ? prices[sym] : pos.avgPrice;
    unreal += unrealizedPnl(pos, price);
    accountMargin += positionMargin(pos, getLeverage(account, sym, settings));
  }
  let ordersMargin = 0;
  for (const id of Object.keys(account.orders)) {
    const o = account.orders[id];
    ordersMargin += orderMargin(o, getLeverage(account, o.symbol, settings));
  }

  const balance = round(account.startingBalance + account.realizedPnl - account.feesPaid);
  const equity = round(balance + unreal);
  accountMargin = round(accountMargin);
  ordersMargin = round(ordersMargin);
  const availableFunds = round(equity - accountMargin - ordersMargin);
  const marginBuffer = equity > 0 ? round(availableFunds / equity, 6) : 0;

  return {
    balance,
    equity,
    realizedPnl: round(account.realizedPnl),
    unrealizedPnl: round(unreal),
    accountMargin,
    ordersMargin,
    availableFunds,
    marginBuffer,
    feesPaid: round(account.feesPaid),
  };
}

// ─── Fill application (mutates the passed account) ──────────────────────────

// Apply a fill of `qty` units at `price` on `side` to the net position.
// Handles increase, partial close (realizing PnL), and reversal through zero.
// Returns { realized, closedQty } for trade-history bookkeeping.
function applyFill(account, symbol, side, qty, price, leverage, now) {
  let pos = account.positions[symbol];
  const fillDir = side === ORDER_SIDE.BUY ? 1 : -1;
  let realized = 0;
  let closedQty = 0;

  if (!pos || pos.qty === 0) {
    account.positions[symbol] = {
      symbol,
      side: fillDir > 0 ? SIDE.LONG : SIDE.SHORT,
      qty: qty,
      avgPrice: price,
      leverage,
      openedAt: now,
    };
    return { realized, closedQty };
  }

  const posDir = pos.side === SIDE.LONG ? 1 : -1;

  if (fillDir === posDir) {
    // Increase: weighted-average the entry.
    const newQty = pos.qty + qty;
    pos.avgPrice = round((pos.avgPrice * pos.qty + price * qty) / newQty);
    pos.qty = round(newQty);
    pos.leverage = leverage;
    return { realized, closedQty };
  }

  // Opposite side — reduce, and possibly reverse.
  const reduce = Math.min(qty, pos.qty);
  closedQty = reduce;
  realized = round(posDir * (price - pos.avgPrice) * reduce);
  account.realizedPnl = round(account.realizedPnl + realized);
  pos.qty = round(pos.qty - reduce);

  if (pos.qty <= 1e-12) {
    // Fully closed. Any leftover opens a reversed position.
    const leftover = round(qty - reduce);
    delete account.positions[symbol];
    if (leftover > 1e-12) {
      account.positions[symbol] = {
        symbol,
        side: fillDir > 0 ? SIDE.LONG : SIDE.SHORT,
        qty: leftover,
        avgPrice: price,
        leverage,
        openedAt: now,
      };
    }
  }

  return { realized, closedQty };
}

function chargeFee(account, notional, rate) {
  const fee = round(notional * rate);
  account.feesPaid = round(account.feesPaid + fee);
  return fee;
}

function recordTradeLeg(account, order, price, qty, realized, now) {
  account.trades.unshift({
    id: nextId("trade"),
    orderId: order.id,
    symbol: order.symbol,
    side: order.side,
    type: order.type,
    role: order.role || (realized !== 0 ? "exit" : "entry"),
    t: now,
    price,
    qty,
    notional: round(price * qty),
    realized: round(realized),
  });
}

// Execute a fill against the book: fee, position update, trade record, activity.
function executeFill(account, order, price, settings, now) {
  const notional = price * order.qty;
  const fee = chargeFee(account, notional, order.type === ORDER_TYPE.MARKET || order.role ? settings.takerFeeRate : settings.makerFeeRate);
  const lev = getLeverage(account, order.symbol, settings);
  const { realized, closedQty } = applyFill(account, order.symbol, order.side, order.qty, price, lev, now);

  order.status = STATUS.FILLED;
  order.fillPrice = round(price);
  order.commission = fee;
  order.filledAt = now;

  recordTradeLeg(account, order, round(price), order.qty, realized, now);
  log(
    account,
    "fill",
    `${order.side === ORDER_SIDE.BUY ? "Buy" : "Sell"} ${order.qty} ${order.symbol} @ ${round(price, 4)} ${order.type.toUpperCase()}`,
    { orderId: order.id, realized: round(realized) },
    now
  );

  // OCO / bracket cleanup.
  handleOcoOnFill(account, order, now);
  cancelOrphanBrackets(account, order.symbol, now);
  return { realized, closedQty, fee };
}

// When one leg of an OCO group fills, cancel its siblings.
function handleOcoOnFill(account, order, now) {
  if (!order.ocoGroup) return;
  for (const id of Object.keys(account.orders)) {
    const o = account.orders[id];
    if (o.id === order.id) continue;
    if (o.ocoGroup === order.ocoGroup && (o.status === STATUS.WORKING || o.status === STATUS.INACTIVE)) {
      o.status = STATUS.CANCELLED;
      o.reason = "OCO sibling filled";
      log(account, "cancel", `${o.role ? o.role.toUpperCase() : "Order"} cancelled (OCO)`, { orderId: o.id }, now);
    }
  }
}

// If a symbol has no open position, cancel its dangling reduce-only brackets.
function cancelOrphanBrackets(account, symbol, now) {
  const pos = account.positions[symbol];
  if (pos && pos.qty > 0) return;
  for (const id of Object.keys(account.orders)) {
    const o = account.orders[id];
    if (o.symbol === symbol && o.reduceOnly && (o.status === STATUS.WORKING || o.status === STATUS.INACTIVE)) {
      o.status = STATUS.CANCELLED;
      o.reason = "position closed";
      log(account, "cancel", `${o.role ? o.role.toUpperCase() : "Bracket"} cancelled (position closed)`, { orderId: o.id }, now);
    }
  }
}

// ─── Order placement / evaluation ───────────────────────────────────────────

// Decide whether a resting order should fill given a quote.
function shouldFill(order, quote) {
  if (order.type === ORDER_TYPE.LIMIT) {
    if (order.side === ORDER_SIDE.BUY) return quote.ask <= order.limitPrice;
    return quote.bid >= order.limitPrice;
  }
  if (order.type === ORDER_TYPE.STOP) {
    // Stop triggers into a market order once price trades through the stop.
    if (order.side === ORDER_SIDE.BUY) return quote.ask >= order.stopPrice;
    return quote.bid <= order.stopPrice;
  }
  return false;
}

// Validate a bracket price sits on the correct side of the entry.
function validateBracket(role, entrySide, entryRef, price) {
  // entrySide is the side of the *position* being protected.
  const long = entrySide === SIDE.LONG;
  if (role === ROLE.TP) {
    if (long && price <= entryRef) return "Take profit must be above entry for a long";
    if (!long && price >= entryRef) return "Take profit must be below entry for a short";
  }
  if (role === ROLE.SL) {
    if (long && price >= entryRef) return "Stop loss must be below entry for a long";
    if (!long && price <= entryRef) return "Stop loss must be above entry for a short";
  }
  return null;
}

// Public: place an order. Returns { state, order, error }.
// spec: { symbol, side, type, qty, limitPrice?, stopPrice?, tif?, reduceOnly?,
//         role?, ocoGroup?, levelId?, brackets?: [{role, price, qty}] }
export function placeOrder(state, spec, quotes = {}, now = Date.now()) {
  const s = clone(state);
  const account = activeAccount(s);
  const settings = s.settings;
  const quote = normQuote(quotes[spec.symbol]);

  const order = {
    id: spec.id || nextId("ord"),
    symbol: spec.symbol,
    side: spec.side,
    type: spec.type,
    qty: spec.qty,
    limitPrice: spec.limitPrice != null ? spec.limitPrice : null,
    stopPrice: spec.stopPrice != null ? spec.stopPrice : null,
    tif: spec.tif || TIF.GTC,
    reduceOnly: !!spec.reduceOnly,
    role: spec.role || null,
    ocoGroup: spec.ocoGroup || null,
    levelId: spec.levelId || null,
    status: STATUS.WORKING,
    fillPrice: null,
    commission: 0,
    placedAt: now,
    filledAt: null,
    reason: null,
  };

  if (!(spec.qty > 0)) {
    order.status = STATUS.REJECTED;
    order.reason = "Quantity must be positive";
    account.orders[order.id] = order;
    log(account, "reject", order.reason, { orderId: order.id }, now);
    return { state: s, order, error: order.reason };
  }

  // Funds check for new-exposure market/limit/stop entries (not reduce-only).
  // Net against an existing opposite position: the closing portion of the order
  // releases margin instead of requiring it, so only genuinely new exposure
  // needs fresh funds.
  if (!order.reduceOnly) {
    const lev = getLeverage(account, order.symbol, settings);
    const refPx =
      order.type === ORDER_TYPE.MARKET
        ? quote
          ? fillPriceFor(order.side, quote, settings.slippageBps)
          : null
        : order.limitPrice != null
        ? order.limitPrice
        : order.stopPrice;
    if (refPx != null) {
      let chargeQty = order.qty;
      const pos = account.positions[order.symbol];
      if (pos && pos.qty > 0) {
        const closingSide = pos.side === SIDE.LONG ? ORDER_SIDE.SELL : ORDER_SIDE.BUY;
        if (order.side === closingSide) {
          chargeQty = Math.max(0, order.qty - pos.qty);
        }
      }
      const needed = round((refPx * chargeQty) / lev);
      const roll = computeAccount(account, pricesFromQuotes(quotes), settings);
      if (needed > roll.availableFunds + 1e-6) {
        order.status = STATUS.REJECTED;
        order.reason = "Insufficient funds";
        account.orders[order.id] = order;
        log(account, "reject", `Order rejected — insufficient funds`, { orderId: order.id }, now);
        return { state: s, order, error: order.reason };
      }
    }
  }

  account.orders[order.id] = order;
  log(account, "order", `${order.type.toUpperCase()} ${order.side} ${order.qty} ${order.symbol} placed`, { orderId: order.id }, now);

  // Market orders fill immediately if we have a quote.
  if (order.type === ORDER_TYPE.MARKET) {
    if (!quote) {
      order.status = STATUS.REJECTED;
      order.reason = "No market price available";
      log(account, "reject", order.reason, { orderId: order.id }, now);
      return { state: s, order, error: order.reason };
    }
    // Enforce reduce-only before the immediate fill (same clamp as onTick).
    if (!applyReduceOnly(account, order)) {
      log(account, "cancel", order.reason, { orderId: order.id }, now);
      return { state: s, order, error: order.reason };
    }
    const px = fillPriceFor(order.side, quote, settings.slippageBps);
    executeFill(account, order, px, settings, now);
    attachBrackets(account, order, spec.brackets, now);
  } else {
    // Resting order — may fill on this very tick if already crossing.
    if (quote && shouldFill(order, quote)) {
      // Enforce reduce-only before the immediate fill (same clamp as onTick).
      if (!applyReduceOnly(account, order)) {
        log(account, "cancel", order.reason, { orderId: order.id }, now);
        return { state: s, order, error: order.reason };
      }
      const px = order.type === ORDER_TYPE.LIMIT ? order.limitPrice : fillPriceFor(order.side, quote, settings.slippageBps);
      executeFill(account, order, px, settings, now);
      attachBrackets(account, order, spec.brackets, now);
    } else {
      order._pendingBrackets = spec.brackets || null; // attach when it fills
    }
  }

  return { state: s, order, error: null };
}

// After an entry fills, create its TP/SL reduce-only OCO orders.
function attachBrackets(account, entryOrder, brackets, now) {
  const list = brackets || entryOrder._pendingBrackets;
  delete entryOrder._pendingBrackets;
  if (!list || !list.length) return;
  const pos = account.positions[entryOrder.symbol];
  if (!pos) return;
  const ocoGroup = nextId("oco");
  const exitSide = pos.side === SIDE.LONG ? ORDER_SIDE.SELL : ORDER_SIDE.BUY;
  for (const b of list) {
    const err = validateBracket(b.role, pos.side, pos.avgPrice, b.price);
    if (err) {
      log(account, "reject", err, {}, now);
      continue;
    }
    const o = {
      id: nextId("ord"),
      symbol: entryOrder.symbol,
      side: exitSide,
      type: b.role === ROLE.TP ? ORDER_TYPE.LIMIT : ORDER_TYPE.STOP,
      qty: b.qty != null ? b.qty : pos.qty,
      limitPrice: b.role === ROLE.TP ? b.price : null,
      stopPrice: b.role === ROLE.SL ? b.price : null,
      tif: TIF.GTC,
      reduceOnly: true,
      role: b.role,
      ocoGroup,
      levelId: b.levelId || nextId("lvl"),
      status: STATUS.WORKING,
      fillPrice: null,
      commission: 0,
      placedAt: now,
      filledAt: null,
      reason: null,
    };
    account.orders[o.id] = o;
    log(account, "order", `${b.role.toUpperCase()} @ ${b.price} attached`, { orderId: o.id }, now);
  }
}

// Modify a working order's price (used by dragging a TP/SL/limit line).
export function modifyOrder(state, orderId, patch = {}, now = Date.now()) {
  const s = clone(state);
  const account = activeAccount(s);
  const o = account.orders[orderId];
  if (!o) return { state: s, error: "No such order" };
  if (o.status !== STATUS.WORKING && o.status !== STATUS.INACTIVE) return { state: s, error: "not modifiable" };
  if (patch.limitPrice != null) o.limitPrice = round(patch.limitPrice, 4);
  if (patch.stopPrice != null) o.stopPrice = round(patch.stopPrice, 4);
  const kind = o.role === ROLE.TP ? "Take Profit" : o.role === ROLE.SL ? "Stop Loss" : "Order";
  log(account, "modify", `${kind} order modified`, { orderId }, now);
  return { state: s, error: null };
}

export function cancelOrder(state, orderId, now = Date.now()) {
  const s = clone(state);
  const account = activeAccount(s);
  const o = account.orders[orderId];
  if (!o) return { state: s, error: "No such order" };
  if (o.status !== STATUS.WORKING && o.status !== STATUS.INACTIVE) {
    return { state: s, error: `Cannot cancel a ${o.status} order` };
  }
  o.status = STATUS.CANCELLED;
  o.reason = "cancelled by user";
  log(account, "cancel", `Order cancelled`, { orderId: o.id }, now);
  return { state: s, error: null };
}

// Close (or partially close) a position at market.
export function closePosition(state, symbol, quotes = {}, opts = {}, now = Date.now()) {
  const s = clone(state);
  const account = activeAccount(s);
  const pos = account.positions[symbol];
  if (!pos || pos.qty === 0) return { state: s, error: "No open position" };
  const quote = normQuote(quotes[symbol]);
  if (!quote) return { state: s, error: "No market price available" };

  const qty = opts.qty != null ? Math.min(opts.qty, pos.qty) : pos.qty;
  const side = pos.side === SIDE.LONG ? ORDER_SIDE.SELL : ORDER_SIDE.BUY;
  const px = fillPriceFor(side, quote, s.settings.slippageBps);

  const order = {
    id: nextId("ord"),
    symbol,
    side,
    type: ORDER_TYPE.MARKET,
    qty,
    limitPrice: null,
    stopPrice: null,
    tif: TIF.GTC,
    reduceOnly: true,
    role: null,
    ocoGroup: null,
    status: STATUS.WORKING,
    fillPrice: null,
    commission: 0,
    placedAt: now,
    filledAt: null,
    reason: null,
  };
  account.orders[order.id] = order;
  executeFill(account, order, px, s.settings, now);
  return { state: s, error: null };
}

// ─── Tick evaluation: resting orders + liquidation ──────────────────────────

function pricesFromQuotes(quotes) {
  const p = {};
  for (const k of Object.keys(quotes || {})) {
    const q = normQuote(quotes[k]);
    if (q) p[k] = q.last;
  }
  return p;
}

// Clamp a reduce-only order to the current position size; cancel it when there
// is no position to reduce. Returns true when the order may proceed to fill.
function applyReduceOnly(account, order) {
  if (!order.reduceOnly) return true;
  const pos = account.positions[order.symbol];
  if (!pos || pos.qty === 0) {
    order.status = STATUS.CANCELLED;
    order.reason = "no position";
    return false;
  }
  order.qty = Math.min(order.qty, pos.qty);
  return true;
}

// Evaluate every account against the latest quotes: fill any crossing
// resting orders, then check for liquidation. Pure. All accounts are evaluated,
// not just the active one, so resting stops and liquidations keep working while
// the user views a different account.
export function onTick(state, quotes = {}, now = Date.now()) {
  const s = clone(state);
  const settings = s.settings;
  for (const id of Object.keys(s.accounts)) {
    tickAccount(s.accounts[id], quotes, settings, now);
  }
  return s;
}

// Evaluate one account against the latest quotes: fill any crossing
// resting orders, then check for liquidation.
function tickAccount(account, quotes, settings, now) {

  // 1) Resting order evaluation. Loop until nothing new fills (a stop can
  //    trigger a fill that closes a position and cancels siblings).
  let changed = true;
  let guard = 0;
  while (changed && guard < 50) {
    changed = false;
    guard += 1;
    for (const id of Object.keys(account.orders)) {
      const o = account.orders[id];
      if (o.status !== STATUS.WORKING) continue;
      const quote = normQuote(quotes[o.symbol]);
      if (!quote) continue;
      if (shouldFill(o, quote)) {
        // reduce-only orders that would flip/exceed the position are clamped.
        if (!applyReduceOnly(account, o)) continue;
        const px = o.type === ORDER_TYPE.LIMIT ? o.limitPrice : fillPriceFor(o.side, quote, settings.slippageBps);
        executeFill(account, o, px, settings, now);
        attachBrackets(account, o, o._pendingBrackets, now);
        changed = true;
      }
    }
  }

  // 2) Liquidation: while margin buffer <= 0, force-close the worst position.
  guard = 0;
  while (guard < 50) {
    guard += 1;
    const roll = computeAccount(account, pricesFromQuotes(quotes), settings);
    if (roll.marginBuffer > 0 || roll.equity <= 0 && Object.keys(account.positions).length === 0) break;
    if (roll.marginBuffer > 0) break;
    const symbols = Object.keys(account.positions);
    if (!symbols.length) break;
    // worst = most negative unrealized pnl
    let worst = null;
    let worstPnl = Infinity;
    for (const sym of symbols) {
      const pos = account.positions[sym];
      const q = normQuote(quotes[sym]);
      const price = q ? q.last : pos.avgPrice;
      const up = unrealizedPnl(pos, price);
      if (up < worstPnl) {
        worstPnl = up;
        worst = sym;
      }
    }
    if (!worst) break;
    const before = Object.keys(account.positions).length;
    const res = liquidateOne(account, worst, quotes, settings, now);
    if (!res || Object.keys(account.positions).length >= before) break; // avoid infinite loop
  }
}

function liquidateOne(account, symbol, quotes, settings, now) {
  const pos = account.positions[symbol];
  if (!pos) return false;
  const quote = normQuote(quotes[symbol]);
  if (!quote) return false;
  const side = pos.side === SIDE.LONG ? ORDER_SIDE.SELL : ORDER_SIDE.BUY;
  const px = fillPriceFor(side, quote, settings.slippageBps);
  const order = {
    id: nextId("ord"),
    symbol,
    side,
    type: ORDER_TYPE.MARKET,
    qty: pos.qty,
    limitPrice: null,
    stopPrice: null,
    tif: TIF.GTC,
    reduceOnly: true,
    role: null,
    ocoGroup: null,
    status: STATUS.WORKING,
    fillPrice: null,
    commission: 0,
    placedAt: now,
    filledAt: now,
    reason: "liquidation",
  };
  account.orders[order.id] = order;
  executeFill(account, order, px, settings, now);
  log(account, "liquidation", `Liquidated ${symbol} @ ${round(px, 4)}`, { orderId: order.id }, now);
  return true;
}

// ─── Account management ─────────────────────────────────────────────────────

export function addAccount(state, { name, startingBalance } = {}, now = Date.now()) {
  const s = clone(state);
  const acct = makeAccount({ name: name || `Paper ${Object.keys(s.accounts).length + 1}`, startingBalance, now });
  s.accounts[acct.id] = acct;
  s.activeAccountId = acct.id;
  return s;
}

export function switchAccount(state, accountId) {
  if (!state.accounts[accountId]) return state;
  const s = clone(state);
  s.activeAccountId = accountId;
  return s;
}

export function resetAccount(state, accountId, opts = {}, now = Date.now()) {
  const s = clone(state);
  const old = s.accounts[accountId];
  if (!old) return s;
  s.accounts[accountId] = makeAccount({
    id: old.id,
    name: old.name,
    startingBalance: opts.startingBalance != null ? opts.startingBalance : old.startingBalance,
    now,
  });
  return s;
}

export function setLeverage(state, symbol, leverage) {
  const s = clone(state);
  const account = activeAccount(s);
  account.leverage[symbol] = Math.max(1, Math.min(100, Math.round(leverage)));
  return s;
}

export function updateSettings(state, patch) {
  const s = clone(state);
  s.settings = { ...s.settings, ...patch };
  return s;
}

export { activeAccount, clone as _clone, round as _round };
